import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));

// Capturador global: en vez de pantalla en blanco, muestra el error real
function showFatalError(msg, titulo) {
  if (document.getElementById("fatal-error-box")) return;
  // Solo si la app realmente no pudo mostrarse (pantalla en blanco); errores sueltos no bloquean el trabajo
  if (document.getElementById("root")?.childElementCount > 0) { console.error(msg); return; }
  const box = document.createElement("div");
  box.id = "fatal-error-box";
  box.style.cssText = "position:fixed;inset:0;z-index:99999;background:#fff;padding:24px;font-family:system-ui;overflow:auto";
  box.innerHTML = `<div style="max-width:480px;margin:80px auto;text-align:center">
    <h1 style="font-size:20px;font-weight:700;color:#0A1F44;margin-bottom:8px">Pya'elino Courier</h1>
    <p style="color:#64748B;font-size:14px;margin-bottom:12px">${titulo || "Ocurrió un error al cargar la aplicación."}</p>
    <pre style="text-align:left;background:#FEF2F2;border:1px solid #FECACA;color:#B91C1C;font-size:11px;padding:12px;border-radius:8px;white-space:pre-wrap;word-break:break-word">${String(msg).replace(/</g, "&lt;")}</pre>
    <button onclick="localStorage.clear();if('caches' in window){caches.keys().then(function(k){k.forEach(function(n){caches.delete(n)})}).finally(function(){location.reload(true)})}else{location.reload(true)}" style="margin-top:16px;background:#0A1F44;color:#fff;border:none;padding:10px 24px;border-radius:8px;font-weight:600;cursor:pointer">Limpiar caché y recargar</button>
  </div>`;
  document.body.appendChild(box);
}
window.addEventListener("error", (e) => {
  // Fallo al cargar un chunk/script de la app (típico de caché vieja tras un redeploy)
  const t = e.target;
  if (t && (t.tagName === "SCRIPT" || t.tagName === "LINK") && t !== window) {
    const src = t.src || t.href || "";
    if (src.startsWith(window.location.origin + "/static/")) showFatalError(`No se pudo cargar: ${src}\nTu navegador tiene una versión vieja en caché.`, "Hay una nueva versión de la aplicación.");
    return;
  }
  // Ignorar "Script error." opaco de scripts de terceros (ruido sin detalle)
  if (!e.message || e.message === "Script error." || !e.filename) return;
  if (!e.filename.startsWith(window.location.origin)) return;
  showFatalError(e.message + `\n${e.filename}:${e.lineno}`);
}, true);
window.addEventListener("unhandledrejection", (e) => {
  const r = e.reason;
  if (r?.name === "ChunkLoadError" || /Loading chunk/i.test(r?.message || "")) showFatalError(r.message, "Hay una nueva versión de la aplicación.");
});

// Aviso de nueva versión: si se publicó una versión nueva, ofrece recargar (sin cortar el trabajo)
if (process.env.NODE_ENV === "production") {
  const actual = document.querySelector('script[src*="/static/js/main."]')?.getAttribute("src");
  const check = () => {
    if (document.getElementById("nueva-version-box") || !actual) return;
    fetch("/", { cache: "no-store" }).then((r) => r.text()).then((html) => {
      const m = html.match(/\/static\/js\/main\.[a-z0-9]+\.js/);
      if (m && m[0] !== actual) {
        const b = document.createElement("div");
        b.id = "nueva-version-box";
        b.style.cssText = "position:fixed;left:50%;bottom:16px;transform:translateX(-50%);z-index:99998;background:#0A1F44;color:#fff;padding:10px 14px;border-radius:12px;font:600 13px system-ui;display:flex;gap:10px;align-items:center;box-shadow:0 10px 30px rgba(0,0,0,.25)";
        b.innerHTML = 'Hay una versión nueva de Pya\'elino. <button style="background:#F5A623;color:#0A1F44;border:none;border-radius:8px;padding:6px 12px;font-weight:700;cursor:pointer">Actualizar</button>';
        b.querySelector("button").onclick = () => location.reload();
        document.body.appendChild(b);
      }
    }).catch(() => {});
  };
  setInterval(check, 5 * 60 * 1000);
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") check(); });
}

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
